import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Check as CheckIcon,
  AttachFile as AttachFileIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import RichTextEditor from '../../RichTextEditor';
import api from '../../../api/axios';

interface TaskEditDialogProps {
  open: boolean;
  task: any | null;
  loading: boolean;
  headers: any;
  availableUsers: any[];

  // Edit state
  editTaskTitle: string;
  editTaskDescription: string;
  editTaskStatus: string;
  editTaskPriority: string;
  editTaskAssignedTo: string;
  editTaskDueDate: string;
  editTaskFiles: File[];
  editTaskExistingAttachments: any[];

  // Setters
  setEditTaskTitle: (title: string) => void;
  setEditTaskDescription: (description: string) => void;
  setEditTaskStatus: (status: string) => void;
  setEditTaskPriority: (priority: string) => void;
  setEditTaskAssignedTo: (userId: string) => void;
  setEditTaskDueDate: (date: string) => void;
  setEditTaskFiles: (files: File[]) => void;
  setEditTaskExistingAttachments: (attachments: any[]) => void;

  // Handlers
  onClose: () => void;
  onSave: () => void;
  onDeleteAttachment: (attachmentId: string) => void;
}

export const TaskEditDialog: React.FC<TaskEditDialogProps> = ({
  open,
  task: _task,
  loading,
  headers,
  availableUsers,
  editTaskTitle,
  editTaskDescription,
  editTaskStatus,
  editTaskPriority,
  editTaskAssignedTo,
  editTaskDueDate,
  editTaskFiles,
  editTaskExistingAttachments,
  setEditTaskTitle,
  setEditTaskDescription,
  setEditTaskStatus,
  setEditTaskPriority,
  setEditTaskAssignedTo,
  setEditTaskDueDate,
  setEditTaskFiles,
  setEditTaskExistingAttachments: _setEditTaskExistingAttachments,
  onClose,
  onSave,
  onDeleteAttachment,
}) => {
  const handleDownloadAttachment = async (attachment: any) => {
    try {
      const response = await api.get(attachment.file_url, { headers });
      const downloadUrl = response.data.url;
      const isLocalUrl = downloadUrl.includes('localhost') || downloadUrl.includes('127.0.0.1');

      if (isLocalUrl) {
        const urlPath = downloadUrl.replace('http://localhost:8000/api', '');
        const fileResponse = await api.get(urlPath, {
          headers,
          responseType: 'blob'
        });

        const blob = new Blob([fileResponse.data]);
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = attachment.original_filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } else {
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = attachment.original_filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Failed to download attachment:', error);
      alert('Failed to download attachment. Please try again.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Edit Task</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ pt: 1 }}>
            {/* Title */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>
                Title
              </Typography>
              <TextField
                size="small"
                fullWidth
                value={editTaskTitle}
                onChange={(e) => setEditTaskTitle(e.target.value)}
                placeholder="Enter task title..."
              />
            </Box>

            {/* Description */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>
                Description
              </Typography>
              <RichTextEditor
                initialContent={editTaskDescription}
                onChange={(content) => setEditTaskDescription(content)}
                placeholder="Enter task description..."
                minHeight={120}
                showSaveButton={false}
              />
            </Box>

            {/* Status and Priority */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>
                  Status
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={editTaskStatus}
                    onChange={(e) => setEditTaskStatus(e.target.value)}
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                    <MenuItem value="blocked">Blocked</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>
                  Priority
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={editTaskPriority}
                    onChange={(e) => setEditTaskPriority(e.target.value)}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Assigned To and Due Date */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>
                  Assigned To
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={editTaskAssignedTo}
                    onChange={(e) => setEditTaskAssignedTo(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>Unassigned</em>
                    </MenuItem>
                    {availableUsers.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.first_name && user.last_name
                          ? `${user.first_name} ${user.last_name}`
                          : user.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>
                  Due Date
                </Typography>
                <TextField
                  type="date"
                  size="small"
                  fullWidth
                  value={editTaskDueDate}
                  onChange={(e) => setEditTaskDueDate(e.target.value)}
                />
              </Box>
            </Box>

            {/* Existing Attachments */}
            {editTaskExistingAttachments.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <AttachFileIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Existing Attachments ({editTaskExistingAttachments.length})
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {editTaskExistingAttachments.map((attachment) => (
                    <Chip
                      key={attachment.id}
                      label={attachment.original_filename}
                      icon={<AttachFileIcon />}
                      onDelete={() => onDeleteAttachment(attachment.id)}
                      onClick={() => handleDownloadAttachment(attachment)}
                      clickable
                      size="small"
                      sx={{
                        '&:hover': {
                          backgroundColor: 'primary.light',
                          color: 'primary.contrastText',
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Add New Attachments */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <UploadIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Add New Attachments
                </Typography>
              </Box>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                size="small"
              >
                Upload Files
                <input
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setEditTaskFiles([...editTaskFiles, ...files]);
                  }}
                  accept=".pdf,.doc,.docx,.txt,.xlsx,.xls,.csv,.png,.jpg,.jpeg"
                />
              </Button>
              {editTaskFiles.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    {editTaskFiles.length} file{editTaskFiles.length !== 1 ? 's' : ''} to upload:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {editTaskFiles.map((file, index) => (
                      <Chip
                        key={index}
                        label={file.name}
                        onDelete={() => setEditTaskFiles(editTaskFiles.filter((_, i) => i !== index))}
                        size="small"
                        icon={<AttachFileIcon sx={{ fontSize: 14 }} />}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={onSave}
          variant="contained"
          startIcon={<CheckIcon />}
          disabled={loading || !editTaskTitle.trim()}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
